import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function SimpleFinSync({ refreshKey, onSyncComplete }) {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState([]);
  const [syncResult, setSyncResult] = useState(null);
  const [setupToken, setSetupToken] = useState('');
  const [setupError, setSetupError] = useState(null);
  const [settingUp, setSettingUp] = useState(false);

  useEffect(() => {
    checkStatus();
  }, [refreshKey]);

  async function checkStatus() {
    try {
      const res = await apiFetch('/api/simplefin/status');
      if (res.ok) {
        const data = await res.json();
        setConnected(data.connected);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSetup(e) {
    e.preventDefault();
    if (!setupToken.trim()) return;
    setSettingUp(true);
    setSetupError(null);
    try {
      const res = await apiFetch('/api/simplefin/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: setupToken.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setConnected(true);
        setSetupToken('');
      } else {
        setSetupError(data.error || 'Setup failed');
      }
    } catch (err) {
      setSetupError(err.message);
    } finally {
      setSettingUp(false);
    }
  }

  async function handleDisconnect() {
    try {
      await apiFetch('/api/simplefin/disconnect', { method: 'DELETE' });
      setConnected(false);
      setSyncResult(null);
    } catch (err) {
      console.error(err);
    }
  }

  async function runSync() {
    setSyncing(true);
    setProgress(['Starting sync...']);
    setSyncResult(null);

    try {
      const res = await apiFetch('/api/simplefin/sync', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setProgress(data.progress || []);
        setSyncResult(data.result);
      } else {
        setProgress((p) => [...p, 'Sync failed']);
      }
    } catch (err) {
      setProgress((p) => [...p, `Error: ${err.message}`]);
    } finally {
      setSyncing(false);
      if (onSyncComplete) onSyncComplete();
    }
  }

  if (loading) {
    return (
      <section className="card">
        <h2>SimpleFIN</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  return (
    <section className="card simplefin-section">
      <div className="goals-header">
        <h2>SimpleFIN</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="manage-btn" onClick={() => window.open('https://beta-bridge.simplefin.org/', '_blank')}>
            Configure
          </button>
          {connected && (
            <button className="manage-btn" onClick={runSync} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Sync Accounts'}
            </button>
          )}
        </div>
      </div>

      {!connected && (
        <div className="sfin-setup">
          <p className="sfin-setup-info">
            Connect your bank accounts via SimpleFIN Bridge. Get a setup token
            from your SimpleFIN account and paste it below.
          </p>
          <form onSubmit={handleSetup} className="sfin-setup-form">
            <input
              type="text"
              value={setupToken}
              onChange={(e) => setSetupToken(e.target.value)}
              placeholder="Paste setup token..."
              className="sfin-setup-input"
              disabled={settingUp}
            />
            <button type="submit" className="manage-btn" disabled={settingUp || !setupToken.trim()}>
              {settingUp ? 'Connecting...' : 'Connect'}
            </button>
          </form>
          {setupError && <div className="sfin-setup-error">{setupError}</div>}
        </div>
      )}

      {connected && (
        <div className="sfin-connected">
          <span className="receipts-account-tag">
            SimpleFIN Connected
            <button className="receipts-reauth" onClick={handleDisconnect} title="Disconnect">&times;</button>
          </span>
        </div>
      )}

      {syncing && (
        <div className="receipts-progress">
          {progress.map((msg, i) => (
            <div key={i} className="progress-line">{msg}</div>
          ))}
          <div className="progress-line progress-active">Working...</div>
        </div>
      )}

      {syncResult && !syncing && (
        <div className="receipts-result">
          <div className="result-stat">
            <span className="result-num">{syncResult.accountsSynced}</span> accounts
          </div>
          <div className="result-stat">
            <span className="result-num">{syncResult.transactionsImported}</span> new txns
          </div>
          <div className="result-stat">
            <span className="result-num">{syncResult.transactionsCached}</span> cached
          </div>
          {syncResult.errors?.length > 0 && (
            <div className="result-errors">
              {syncResult.errors.map((e, i) => (
                <div key={i} className="result-error">{e}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default SimpleFinSync;
